(function () {
  "use strict";

  const EVENT_NAMES = Object.freeze({
    section_view: "section_view",
    section_dwell: "section_dwell",
    select_promotion: "select_promotion",
    view_item_list: "view_item_list",
    select_item: "select_item",
    newsletter_submit: "newsletter_submit",
    form_success: "form_success",
    form_error: "form_error",
  });

  const PAGE_SECTION_SELECTOR =
    '#MainContent > [id^="shopify-section-"]';
  const EXPOSURE_THRESHOLD = 0.5;
  const EXPOSURE_DELAY = 1000;
  const analyticsScript = document.currentScript;
  const PAGE_TYPE =
    analyticsScript?.dataset?.analyticsPageType ||
    Array.from(document.scripts).find(
      (script) => script.dataset.analyticsPageType
    )?.dataset.analyticsPageType ||
    (document.body.classList.contains("index") ? "home" : "");

  /**
   * 将页面模块事件统一发送到现有 GTM dataLayer，避免每个 section 自己维护上报格式。
   * @param {string} eventName - 埋点事件名称
   * @param {Object} parameters - 事件参数
   * @returns {void}
   */
  function publishEvent(eventName, parameters) {
    if (!eventName || typeof window === "undefined") return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      page_type: PAGE_TYPE,
      ...parameters,
    });
  }

  /**
   * 获取 section 的稳定标识。优先读取 Liquid 输出的 section ID，兼容 Shopify 外层包装 ID。
   * @param {Element} element - 当前元素
   * @returns {Object} section 上下文
   */
  function getSectionContext(element) {
    const section =
      element?.closest?.("[data-homepage-section-id]") ||
      element?.querySelector?.("[data-homepage-section-id]");
    const wrappedSection =
      section || element?.closest?.('[id^="shopify-section-"]');
    const firstChildClass =
      typeof wrappedSection?.firstElementChild?.className === "string"
        ? wrappedSection.firstElementChild.className.split(/\s+/)[0]
        : "";
    const sectionId =
      wrappedSection?.dataset?.homepageSectionId ||
      wrappedSection?.id?.replace("shopify-section-", "") ||
      "unknown";
    const sectionType =
      wrappedSection?.dataset?.homepageSectionType ||
      section?.dataset?.homepageSectionType ||
      firstChildClass ||
      "unknown";
    const sectionHeading = wrappedSection?.querySelector?.(
      "h1, h2, h3, h4, h5, h6, [role='heading']"
    );

    return {
      section_id: sectionId,
      section_type: sectionType,
      section_name: getPlainText(sectionHeading),
    };
  }

  /**
   * 只对同一个 DOM 元素上报一次曝光，防止滚动来回造成重复数据。
   * @param {Element} element - 要观察的元素
   * @param {Function} onVisible - 满足可见条件后的回调
   * @returns {void}
   */
  function observeOnce(element, onVisible) {
    if (!element || element.dataset.homepageExposureTracked === "true") return;

    const markAndPublish = () => {
      if (element.dataset.homepageExposureTracked === "true") return;
      element.dataset.homepageExposureTracked = "true";
      onVisible();
    };

    if (!("IntersectionObserver" in window)) {
      const rectangle = element.getBoundingClientRect();
      if (rectangle.bottom > 0 && rectangle.top < window.innerHeight) {
        markAndPublish();
      }
      return;
    }

    let timer = null;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;

        if (
          entry.isIntersecting &&
          entry.intersectionRatio >= EXPOSURE_THRESHOLD
        ) {
          if (timer) return;
          timer = window.setTimeout(() => {
            observer.disconnect();
            markAndPublish();
          }, EXPOSURE_DELAY);
          return;
        }

        if (timer) {
          window.clearTimeout(timer);
          timer = null;
        }
      },
      { threshold: [EXPOSURE_THRESHOLD] }
    );

    observer.observe(element);
  }

  function getPlainText(element) {
    return element?.textContent?.replace(/\s+/g, " ").trim() || "";
  }

  /**
   * 统计每个页面 section 的首次曝光和可见停留时间。
   * section_view 只上报一次；section_dwell 在用户离开模块或页面隐藏时上报本次停留时长。
   * @returns {void}
   */
  function trackSectionEngagement() {
    if (!("IntersectionObserver" in window)) return;

    const sections = Array.from(
      document.querySelectorAll(PAGE_SECTION_SELECTOR)
    );

    sections.forEach((section, sectionIndex) => {
      if (section.dataset.homepageEngagementBound === "true") return;
      section.dataset.homepageEngagementBound = "true";
      const sectionPosition = sectionIndex + 1;

      const state = {
        exposureTimer: null,
        enteredAt: 0,
        cumulativeMilliseconds: 0,
        viewCount: 0,
        isVisible: false,
        hasViewed: false,
      };
      section.__homepageEngagementState = state;

      const reportView = () => {
        if (state.hasViewed || !state.isVisible) return;
        state.hasViewed = true;
        publishEvent(EVENT_NAMES.section_view, {
          ...getSectionContext(section),
          section_position: sectionPosition,
          view_count: state.viewCount,
        });
      };

      const reportDwell = (exitReason) => {
        if (!state.enteredAt) return;

        const durationMilliseconds = Math.max(
          0,
          performance.now() - state.enteredAt
        );
        state.enteredAt = 0;
        state.cumulativeMilliseconds += durationMilliseconds;

        if (durationMilliseconds < EXPOSURE_DELAY) return;
        publishEvent(EVENT_NAMES.section_dwell, {
          ...getSectionContext(section),
          section_position: sectionPosition,
          duration_seconds: Number((durationMilliseconds / 1000).toFixed(1)),
          cumulative_duration_seconds: Number(
            (state.cumulativeMilliseconds / 1000).toFixed(1)
          ),
          view_count: state.viewCount,
          exit_reason: exitReason,
        });
      };

      const flushDwell = (exitReason) => {
        if (state.exposureTimer) {
          window.clearTimeout(state.exposureTimer);
          state.exposureTimer = null;
        }
        if (!state.isVisible) return;
        if (
          !state.hasViewed &&
          state.enteredAt &&
          performance.now() - state.enteredAt >= EXPOSURE_DELAY
        ) {
          reportView();
        }
        state.isVisible = false;
        reportDwell(exitReason);
      };

      state.flushDwell = flushDwell;

      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;

          const isVisible =
            entry.isIntersecting &&
            entry.intersectionRatio >= EXPOSURE_THRESHOLD;

          if (isVisible && !state.isVisible) {
            state.isVisible = true;
            state.enteredAt = performance.now();
            state.viewCount += 1;
            state.exposureTimer = window.setTimeout(() => {
              state.exposureTimer = null;
              reportView();
            }, EXPOSURE_DELAY);
            return;
          }

          if (!isVisible && state.isVisible) {
            flushDwell("section_exit");
          }
        },
        { threshold: [0, EXPOSURE_THRESHOLD] }
      );

      observer.observe(section);
    });

    if (window.__homepageSectionLifecycleBound) return;
    window.__homepageSectionLifecycleBound = true;

    const flushVisibleSections = (exitReason) => {
      document.querySelectorAll(PAGE_SECTION_SELECTOR).forEach((section) => {
        section.__homepageEngagementState?.flushDwell?.(exitReason);
      });
    };

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        flushVisibleSections("page_hidden");
      }
    });
    window.addEventListener("pagehide", () => {
      flushVisibleSections("page_hide");
    });
  }

  function getPromotionContext(slide) {
    const sectionContext = getSectionContext(slide);
    const slideIndex = Array.from(
      slide.parentElement?.children || []
    ).indexOf(slide);

    return {
      ...sectionContext,
      promotion_id: slide.dataset.slideId || `slide_${slideIndex + 1}`,
      promotion_name:
        slide.dataset.promotionName ||
        getPlainText(slide.querySelector(".index-banner-title")),
      creative_slot: slideIndex >= 0 ? slideIndex + 1 : undefined,
    };
  }

  function trackHeroButtonClicks() {
    const hero = document.querySelector("index-banner-slider");
    if (!hero || hero.dataset.homepagePromotionClicksBound === "true") return;

    hero.dataset.homepagePromotionClicksBound = "true";
    hero.addEventListener("click", (event) => {
      const link = event.target.closest?.(".index-banner-button");
      const slide = link?.closest?.(".index-banner-slide");
      if (!link || !slide) return;

      publishEvent(EVENT_NAMES.select_promotion, {
        ...getPromotionContext(slide),
        link_url: link.href,
        link_text: getPlainText(link),
      });
    });
  }

  function getProductItemData(item) {
    const link = item.querySelector("a[href]");
    let itemUrl = "";
    let itemHandle = item.dataset.productHandle || "";

    if (link) {
      itemUrl = link.href;
      try {
        const productUrl = new URL(link.href, window.location.href);
        const productPath = productUrl.pathname.match(/^\/products\/([^/]+)/);
        if (productPath) itemHandle = itemHandle || productPath[1];
      } catch (error) {
        itemUrl = link.getAttribute("href") || "";
      }
    }

    return {
      item_id: itemHandle || item.dataset.productName || "unknown",
      item_name:
        item.dataset.productName ||
        getPlainText(item.querySelector(".zl-home-ps-item-title")),
      item_list_id: "homepage_products",
      item_list_name: "Homepage products",
      index: Number(item.dataset.itemPosition || 0),
      item_url: itemUrl,
    };
  }

  function trackProductList() {
    const productSection = document.querySelector(
      "[data-homepage-product-section]"
    );
    if (!productSection) return;

    observeOnce(productSection, () => {
      const items = Array.from(
        productSection.querySelectorAll("[data-homepage-product-item]")
      ).map(getProductItemData);

      if (items.length === 0) return;
      publishEvent(EVENT_NAMES.view_item_list, {
        ...getSectionContext(productSection),
        item_list_id: "homepage_products",
        item_list_name: "Homepage products",
        items,
      });
    });

    if (productSection.dataset.homepageProductClicksBound === "true") return;
    productSection.dataset.homepageProductClicksBound = "true";
    productSection.addEventListener("click", (event) => {
      const link = event.target.closest?.("a[href]");
      const item = link?.closest?.("[data-homepage-product-item]");
      if (!link || !item) return;

      publishEvent(EVENT_NAMES.select_item, {
        ...getSectionContext(productSection),
        items: [getProductItemData(item)],
      });
    });
  }

  function getFormContext(form) {
    return {
      ...getSectionContext(form),
      form_id: form.id || "homepage_newsletter",
      form_type: "newsletter",
    };
  }

  function trackNewsletter() {
    document
      .querySelectorAll("[data-homepage-newsletter]")
      .forEach((section) => {
        const form = section.querySelector("form");
        if (!form) return;

        if (form.dataset.homepageNewsletterBound !== "true") {
          form.dataset.homepageNewsletterBound = "true";
          form.addEventListener("submit", () => {
            publishEvent(EVENT_NAMES.newsletter_submit, getFormContext(form));
          });
        }

        const stateMarker = form.querySelector(
          "[data-homepage-form-state]"
        );
        const state = stateMarker?.dataset.homepageFormState;
        if (state === "success" || state === "error") {
          const stateKey = `homepageFormStateTracked${state}`;
          if (form.dataset[stateKey] === "true") return;
          form.dataset[stateKey] = "true";
          publishEvent(
            state === "success"
              ? EVENT_NAMES.form_success
              : EVENT_NAMES.form_error,
            getFormContext(form)
          );
        }
      });
  }

  function initialize() {
    if (!PAGE_TYPE) return;

    trackSectionEngagement();
    if (PAGE_TYPE !== "home") return;

    trackHeroButtonClicks();
    trackProductList();
    trackNewsletter();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener("shopify:section:load", initialize);
})();
